<template>
  <div class="service-page" v-if="service">
    <section class="service-hero" aria-labelledby="service-title">
      <img
        class="service-hero-bg"
        :src="service.heroImage"
        :alt="service.title + '对公工程查验服务场景'"
      />
      <div class="service-hero-overlay"></div>
      <div class="service-hero-content">
        <nav class="breadcrumb" aria-label="面包屑">
          <router-link to="/">首页</router-link>
          <span>/</span>
          <router-link to="/services">对公服务</router-link>
          <span>/</span>
          <span>{{ service.shortTitle }}</span>
        </nav>
        <p class="eyebrow">对公工程查验服务</p>
        <h1 id="service-title">{{ service.title }}</h1>
        <p>{{ service.summary }}</p>
        <router-link class="btn btn-primary" :to="{ path: '/', hash: '#lead-form' }">咨询服务方案</router-link>
      </div>
    </section>

    <section class="section intro-section" aria-labelledby="intro-title">
      <div class="section-inner intro-layout">
        <div>
          <p class="section-kicker">Service Introduction</p>
          <h2 id="intro-title">服务介绍</h2>
          <p class="intro">{{ service.summary }}</p>
        </div>
        <img :src="service.image" :alt="service.shortTitle + '服务现场示意图'" />
      </div>
    </section>

    <section class="section muted" aria-labelledby="target-title">
      <div class="section-inner two-column">
        <div>
          <p class="section-kicker">Service Object</p>
          <h2 id="target-title">适用对象</h2>
          <p class="intro">适用于需要第三方查验、检测记录、问题清单和整改复查闭环的 B 端项目客户。</p>
        </div>
        <ul class="tag-list">
          <li v-for="item in service.targetUsers" :key="item">{{ item }}</li>
        </ul>
      </div>
    </section>

    <section class="section" aria-labelledby="scenario-title">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">Applicable Scenes</p>
          <h2 id="scenario-title">服务场景</h2>
          <p>根据项目进度、楼栋范围、交付节点和现场管理目标，确定查验范围与人员安排。</p>
        </div>
        <div class="card-grid">
          <article class="info-card" v-for="item in service.scenarios" :key="item">
            <i class="el-icon-location-outline"></i>
            <h3>{{ item }}</h3>
          </article>
        </div>
      </div>
    </section>

    <section class="section muted" aria-labelledby="content-title">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">Inspection Content</p>
          <h2 id="content-title">查验内容</h2>
          <p>查验范围会根据项目类型、交付标准、进场条件和质量管理目标进行细化。</p>
        </div>
        <ul class="check-list">
          <li v-for="item in service.checkItems" :key="item">
            <i class="el-icon-check"></i>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
    </section>

    <section class="section process-section" aria-labelledby="flow-title">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">Workflow</p>
          <h2 id="flow-title">服务流程</h2>
        </div>
        <ol class="process-list">
          <li v-for="(step, index) in process" :key="step.title">
            <span>{{ index + 1 }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </li>
        </ol>
      </div>
    </section>

    <section class="section" aria-labelledby="deliver-title">
      <div class="section-inner two-column deliver-layout">
        <div>
          <p class="section-kicker">Deliverables</p>
          <h2 id="deliver-title">交付成果</h2>
          <p class="intro">输出内容强调可追溯、可整改、可复查，便于项目团队推进质量闭环。</p>
        </div>
        <ul class="deliver-list">
          <li v-for="item in service.deliverables" :key="item">
            <i class="el-icon-document-checked"></i>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
    </section>

    <section class="section muted" aria-labelledby="related-case-title" v-if="relatedCases.length">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">Related Cases</p>
          <h2 id="related-case-title">项目经验 / 案例入口</h2>
          <p>结合类似项目服务记录，了解查验、记录、报告与整改复查如何落到项目现场。</p>
        </div>
        <div class="case-grid">
          <article class="case-card" v-for="item in relatedCases" :key="item.id">
            <img :src="item.image" :alt="item.title + '服务场景示意图'" />
            <div>
              <span>{{ item.city }}｜{{ item.projectType }}</span>
              <h3>{{ item.title }}</h3>
              <p>{{ item.value }}</p>
              <router-link :to="'/cases/' + item.id">查看案例</router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="section" aria-labelledby="service-faq-title">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">FAQ</p>
          <h2 id="service-faq-title">常见问题</h2>
        </div>
        <div class="faq-list">
          <details v-for="item in service.faqs" :key="item.question">
            <summary>{{ item.question }}</summary>
            <p>{{ item.answer }}</p>
          </details>
        </div>
      </div>
    </section>

    <section class="service-cta" aria-labelledby="service-cta-title">
      <div class="section-inner">
        <h2 id="service-cta-title">需要为项目制定{{ service.shortTitle }}方案？</h2>
        <p>提供项目城市、预计户数、当前节点、楼栋范围和报告要求后，我们可以进一步沟通人员配置、进场安排和报告交付方式。</p>
        <router-link class="btn btn-secondary" :to="{ path: '/', hash: '#lead-form' }">获取项目报价</router-link>
      </div>
    </section>

    <div class="beian">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">{{ company.icp }}</a>
      <a :href="company.publicSecurityUrl" target="_blank" rel="noopener">{{ company.publicSecurityRecord }}</a>
    </div>
  </div>
</template>

<script>
import { companyInfo, detailedCases, serviceProcessDetails, services } from "../data/services";
import {
  absoluteUrl,
  breadcrumbSchema,
  localBusinessSchema,
  organizationSchema,
  setSeo,
  setStructuredData
} from "../utils/seo";

export default {
  name: "ServiceDetail",
  data() {
    return {
      company: companyInfo,
      process: serviceProcessDetails
    };
  },
  computed: {
    service() {
      const slug = this.$route.params.slug;
      return services.find(function(item) {
        return item.slug === slug;
      });
    },
    relatedCases() {
      if (!this.service) {
        return [];
      }
      return detailedCases.filter(item => item.relatedServices.indexOf(this.service.slug) !== -1).slice(0, 3);
    }
  },
  mounted() {
    this.applyPageSeo();
  },
  watch: {
    "$route.params.slug"() {
      this.applyPageSeo();
    }
  },
  methods: {
    applyPageSeo() {
      if (!this.service) {
        this.$router.replace("/services");
        return;
      }

      setSeo({
        title: this.service.title + "｜对公工程查验｜江苏道鼎升工程检测",
        description: this.service.metaDescription,
        path: this.service.path,
        image: this.service.heroImage
      });

      setStructuredData([
        organizationSchema(),
        localBusinessSchema(),
        {
          "@type": "Service",
          "@id": absoluteUrl(this.service.path + "#service"),
          name: this.service.shortTitle,
          serviceType: this.service.serviceType,
          description: this.service.summary,
          provider: {
            "@id": absoluteUrl("/#organization")
          },
          areaServed: companyInfo.serviceArea,
          url: absoluteUrl(this.service.path)
        },
        breadcrumbSchema([
          { name: "首页", path: "/" },
          { name: "对公服务", path: "/services" },
          { name: this.service.shortTitle, path: this.service.path }
        ])
      ]);
    }
  }
};
</script>

<style lang="scss" scoped>
.service-page {
  background: #fff;
  color: #1f2d3d;
}

.service-hero {
  min-height: 460px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.service-hero-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.service-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(7, 25, 40, 0.94), rgba(12, 58, 83, 0.72), rgba(12, 58, 83, 0.28));
}

.service-hero-content,
.section-inner {
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
}

.service-hero-content {
  position: relative;
  z-index: 1;
  color: #fff;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 28px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 14px;
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
}

.eyebrow,
.section-kicker {
  color: #c8872a;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.service-hero h1 {
  max-width: 860px;
  font-size: 42px;
  line-height: 1.25;
  margin: 0 0 18px;
}

.service-hero p {
  max-width: 900px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 17px;
  line-height: 1.9;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0 24px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 16px;
  font-weight: 700;
  margin-top: 18px;
}

.btn-primary {
  color: #fff;
  background: #0c4a6e;
}

.btn-secondary {
  color: #fff;
  background: #c8872a;
}

.section {
  padding: 76px 0;
}

.muted {
  background: #f4f6f8;
}

.intro-layout,
.two-column {
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 42px;
  align-items: center;
}

.intro-layout img {
  width: 100%;
  min-height: 360px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 22px 48px rgba(15, 45, 66, 0.12);
}

.section-heading {
  max-width: 760px;
  margin-bottom: 30px;
}

h2 {
  color: #102d42;
  font-size: 31px;
  line-height: 1.35;
  margin: 0 0 14px;
}

.intro,
.section-heading p {
  color: #5e6b76;
  line-height: 1.8;
  font-size: 16px;
}

.tag-list,
.check-list,
.deliver-list,
.process-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.tag-list li {
  background: #eef5f8;
  color: #0c4a6e;
  border: 1px solid #cfdce6;
  border-radius: 4px;
  padding: 11px 16px;
  font-weight: 700;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.info-card,
.check-list li,
.deliver-list li,
.case-card,
.faq-list details {
  background: #fff;
  border: 1px solid #dfe6eb;
  border-radius: 8px;
}

.info-card {
  min-height: 150px;
  padding: 24px;
}

.info-card i {
  color: #c8872a;
  font-size: 24px;
}

.info-card h3 {
  color: #102d42;
  font-size: 18px;
  line-height: 1.5;
  margin: 18px 0 0;
}

.check-list,
.deliver-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.check-list li,
.deliver-list li {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 18px;
  line-height: 1.7;
  color: #3d4b56;
}

.check-list i,
.deliver-list i {
  color: #0c4a6e;
  font-size: 20px;
  margin-top: 3px;
}

.process-section {
  background: #102d42;
  color: #fff;
}

.process-section h2,
.process-section .section-heading p {
  color: #fff;
}

.process-section .section-heading p {
  color: rgba(255, 255, 255, 0.78);
}

.process-list {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
}

.process-list li {
  min-height: 178px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #fff;
  border-radius: 8px;
}

.process-list span {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  background: #c8872a;
  font-weight: 700;
  margin-bottom: 16px;
}

.process-list h3 {
  color: #fff;
  margin: 0;
  font-size: 18px;
}

.process-list p {
  color: rgba(255, 255, 255, 0.78);
  font-size: 14px;
  line-height: 1.7;
  margin: 10px 0 0;
}

.deliver-layout {
  align-items: center;
}

.case-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.case-card {
  overflow: hidden;
}

.case-card img {
  width: 100%;
  height: 190px;
  object-fit: cover;
  display: block;
}

.case-card div {
  padding: 22px;
}

.case-card span {
  color: #0c4a6e;
  font-size: 13px;
  font-weight: 700;
}

.case-card h3 {
  color: #102d42;
  font-size: 20px;
  line-height: 1.45;
  margin: 12px 0;
}

.case-card p {
  color: #5e6b76;
  line-height: 1.75;
  margin: 0 0 16px;
}

.case-card a {
  color: #0c4a6e;
  font-weight: 700;
  text-decoration: none;
}

.faq-list {
  display: grid;
  gap: 12px;
}

.faq-list details {
  padding: 0 22px;
}

.faq-list summary {
  cursor: pointer;
  list-style: none;
  padding: 20px 0;
  color: #102d42;
  font-size: 17px;
  font-weight: 700;
}

.faq-list summary::-webkit-details-marker {
  display: none;
}

.faq-list p {
  border-top: 1px solid #edf2f1;
  color: #5e6b76;
  line-height: 1.8;
  padding: 16px 0 20px;
  margin: 0;
}

.service-cta {
  background: #102d42;
  color: #fff;
  padding: 68px 0;
}

.service-cta h2 {
  color: #fff;
}

.service-cta p {
  max-width: 820px;
  color: rgba(255, 255, 255, 0.86);
  line-height: 1.8;
}

@media (max-width: 1080px) {
  .service-hero h1 {
    font-size: 34px;
  }

  .intro-layout,
  .two-column,
  .card-grid,
  .case-grid,
  .check-list,
  .deliver-list,
  .process-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .service-hero-content,
  .section-inner {
    width: min(100% - 28px, 1180px);
  }

  .service-hero {
    min-height: auto;
    padding: 70px 0;
  }

  .service-hero h1 {
    font-size: 28px;
  }

  .section {
    padding: 54px 0;
  }

  h2 {
    font-size: 25px;
  }
}
</style>
